import type { OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Image } from '@core-mkt/interfaces/image';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import {
  ensureTrailingSlash,
  getParsedLinkString,
  getQueryParamString,
} from '@core-mkt/services/query-param/query-helpers';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import type { VisionButton } from '../../vision-button/vision-button/vision-button';
import { EntryListingArticleCard } from '../entry-listings/entry-listings';

@Component({
  selector: 'entry-listing-article-card',
  templateUrl: './entry-listing-article-card.component.html',
  styleUrls: ['./entry-listing-article-card.component.scss'],
  standalone: true,
  imports: [VisionButtonComponent, LazyImgDirective],
})
export class EntryListingArticleCardComponent implements OnInit {
  @Input() data: EntryListingArticleCard;
  resourceTextContent = undefined;
  currentCategory = undefined;

  private redactor = inject(WysiwygRedactorService);
  imgixService = inject(ImgixService);
  private route = inject(ActivatedRoute);
  private queryParamService = inject(QueryParamService);
  private brand = inject(BrandConfigurationService);
  private env = inject(EnvService);

  get image(): Image | undefined {
    return this.data.image?.[0];
  }

  get relatedResourceContent(): string | undefined {
    return this.data.relatedResourceContent;
  }

  get dateCreated(): string | undefined {
    return this.data.dateCreated;
  }

  get relatedResourcesCategory(): string | undefined {
    let indexCategory = 0;
    if (this.currentCategory) {
      const foundIndex = this.data.relatedResourcesCategory?.findIndex((u) => u.slug === this.currentCategory);
      if (foundIndex !== undefined && foundIndex !== -1) {
        indexCategory = foundIndex;
      }
    }
    if (this.data.state && this.data.relatedResourcesCategory?.length > 0) {
      return this.data.relatedResourcesCategory[indexCategory]?.title;
    }
    const relatedResourcesCategory = this.data.relatedResourcesCategory;
    const entryListingArticleCards = this.data.entryListingArticleCards;

    if (relatedResourcesCategory?.length === 0 || entryListingArticleCards?.length === 0) {
      return '';
    } else if (relatedResourcesCategory?.length > 0) {
      return relatedResourcesCategory[indexCategory]?.title;
    } else if (entryListingArticleCards?.length > 0) {
      if (this.currentCategory) {
        const foundIndex = entryListingArticleCards?.findIndex((u) => u.slug === this.currentCategory);
        if (foundIndex !== undefined && foundIndex !== -1) {
          indexCategory = foundIndex;
        }
      }
      return entryListingArticleCards[indexCategory]?.title;
    }
  }

  get relatedResourceLink(): string | undefined {
    if (!this.data.relatedResourceLink) {
      return this.data.relatedResourceLink;
    }
    return this.env.get.enforceTrailingSlash
      ? ensureTrailingSlash(this.data.relatedResourceLink)
      : this.data.relatedResourceLink;
  }

  get relatedResourceCtaLabel(): string | undefined {
    return this.data.relatedResourceCtaLabel;
  }

  get ctaButtonData(): VisionButton {
    return {
      buttonStyle: 'tertiary',
      bgTheme: 'bg-white',
      buttonText: this.relatedResourceCtaLabel || 'Read Full Article',
      buttonLink: this.relatedResourceLink,
      fullWidth: false,
      nofollow: false,
      noreferrer: false,
      noopener: false,
      newTab: false,
      useIcon: true,
    };
  }

  ngOnInit(): void {
    const segments = this.route.snapshot.url;
    this.currentCategory = segments.length > 0 ? segments[0].path : undefined;
  }

  getTargetUrl(): string | undefined {
    if (!this.relatedResourceLink) {
      return undefined;
    }
    const queryParams = this.queryParamService.getQueryParams();
    const blogPrefix = this.env.get.blogPrefix;
    const queryString = getQueryParamString(queryParams, this.relatedResourceLink, blogPrefix);
    return getParsedLinkString(
      this.relatedResourceLink,
      queryString,
      this.brand.hostName,
      this.brand.siblingSites,
      blogPrefix,
    );
  }

  articleRoute(): void {
    const target = this.getTargetUrl();
    if (target) {
      window.location.href = target;
    }
  }
}
